import axios from "axios";
import { getToken } from "../services/storageService";
import {
  gatewayBaseUrlFromEnv,
  resolveWebSocketGatewayOrigin,
} from "../runtime/loadSharedDashboardConfig";

export const apiClient = axios.create({
  headers: { "Content-Type": "application/json" },
  withCredentials: false,
});

apiClient.interceptors.request.use((config) => {
  config.baseURL = gatewayBaseUrlFromEnv();
  const token = getToken();
  if (token) {
    config.headers = config.headers ?? {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

let overviewWs: WebSocket | null = null;
let overviewWsPromise: Promise<WebSocket> | null = null;

const pendingRequests = new Map<
  string,
  {
    resolve: (value: any) => void;
    reject: (reason?: any) => void;
    timeout: ReturnType<typeof setTimeout>;
  }
>();

/** Gateway pushes `events_indexed` when a surveillance event is written to ES (see api-gateway Redis subscriber). */
export type EventsIndexedPayload = {
  clientId?: string;
  eventId?: string;
  locationId?: string;
  indexedAt?: string;
};

const eventsIndexedListeners = new Set<(payload: EventsIndexedPayload) => void>();
const newEventListeners = new Set<(payload: any) => void>();

export function subscribeEventsIndexed(
  cb: (payload: EventsIndexedPayload) => void,
): () => void {
  eventsIndexedListeners.add(cb);
  return () => {
    eventsIndexedListeners.delete(cb);
  };
}

export function subscribeNewEvent(cb: (payload: any) => void): () => void {
  newEventListeners.add(cb);
  return () => {
    newEventListeners.delete(cb);
  };
}

/** Avoid mixed content: never use ws:// from an https origin. */
const upgradeWsIfPageIsHttps = (url: string): string => {
  if (
    typeof window !== "undefined" &&
    window.location.protocol === "https:" &&
    url.startsWith("ws://")
  ) {
    return `wss://${url.slice("ws://".length)}`;
  }
  return url;
};

const getWsBaseUrl = (): string => upgradeWsIfPageIsHttps(resolveWebSocketGatewayOrigin());

/**
 * Gateway handles WS at `/surveillance-overview` only. Some deployments set
 * `REACT_APP_WS_GATEWAY_URL` to `…/ws/surveillance`, which produced
 * `…/ws/surveillance/surveillance-overview` and a failed upgrade (404).
 */
const normalizeOverviewWsBase = (base: string): string => {
  const trimmed = base.replace(/\/$/, "");
  try {
    const u = new URL(trimmed);
    const path = u.pathname.replace(/\/$/, "") || "";
    if (path === "/ws/surveillance/surveillance-overview") {
      return `${u.protocol}//${u.host}/surveillance-overview`;
    }
    if (path === "/ws/surveillance") {
      return `${u.protocol}//${u.host}`;
    }
    if (path.endsWith("/surveillance-overview")) {
      return trimmed;
    }
  } catch {
    /* ignore */
  }
  return trimmed;
};

const ensureOverviewWebSocket = (): Promise<WebSocket> => {
  if (overviewWs && overviewWs.readyState === WebSocket.OPEN) {
    return Promise.resolve(overviewWs);
  }

  if (overviewWsPromise) {
    return overviewWsPromise;
  }

  const wsBase = normalizeOverviewWsBase(getWsBaseUrl()).replace(/\/$/, "");
  /** Env may be the full gateway path; avoid duplicating the path segment. */
  const overviewSuffix = "/surveillance-overview";
  const url = wsBase.endsWith(overviewSuffix)
    ? wsBase
    : `${wsBase}${overviewSuffix}`;

  overviewWsPromise = new Promise<WebSocket>((resolve, reject) => {
    try {
      const ws = new WebSocket(url);
      overviewWs = ws;

      ws.onopen = () => {
        resolve(ws);
      };

      ws.onerror = () => {
        reject(new Error("WebSocket error"));
      };

      ws.onclose = () => {
        overviewWs = null;
        overviewWsPromise = null;
        pendingRequests.forEach(({ reject, timeout }) => {
          clearTimeout(timeout);
          reject(new Error("WebSocket closed"));
        });
        pendingRequests.clear();
      };

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          const { type, requestId, statusCode, payload } = msg || {};

          if (type === "events_indexed" && payload && eventsIndexedListeners.size > 0) {
            eventsIndexedListeners.forEach((fn) => {
              try {
                fn(payload as EventsIndexedPayload);
              } catch {
                /* listener error — ignore */
              }
            });
          }

          if (type === "new_event" && payload && newEventListeners.size > 0) {
            newEventListeners.forEach((fn) => {
              try {
                fn(payload);
              } catch {
                /* listener error — ignore */
              }
            });
          }

          if (requestId && pendingRequests.has(requestId)) {
            const entry = pendingRequests.get(requestId)!;
            clearTimeout(entry.timeout);
            pendingRequests.delete(requestId);
            if (statusCode >= 200 && statusCode < 300) {
              entry.resolve(payload);
            } else {
              entry.reject(payload || new Error("WebSocket request error"));
            }
          }
        } catch {
          // ignore malformed
        }
      };
    } catch (err) {
      overviewWsPromise = null;
      reject(err);
    }
  });

  return overviewWsPromise;
};

/**
 * Generic websocket request for realtime pages that should not reuse
 * the overview page's request `type`.
 *
 * Backend differentiates handlers using the `type` field.
 */
const getRealtimeEventsViaWebSocket = async <T = any>(
  requestType: string,
  payload: any
): Promise<T> => {
  const ws = await ensureOverviewWebSocket();
  const requestId = Math.random().toString(36).slice(2);

  return new Promise<T>((resolve, reject) => {
    const timeout = setTimeout(() => {
      pendingRequests.delete(requestId);
      reject(new Error(`realtime_events_response timeout (${requestType})`));
    }, 15000);

    pendingRequests.set(requestId, { resolve, reject, timeout });

    const message = JSON.stringify({
      type: requestType,
      requestId,
      payload,
    });

    ws.send(message);
  });
};

export const postRequest = async <T = any>(endPoint: string, payload?: any): Promise<T> => {
  const res = await apiClient.post<T>(endPoint, payload);
  return res.data;
};

export const getRequest = async <T = any>(endPoint: string, params?: any): Promise<T> => {
  /** Overview WebSocket bridge disabled — use plain HTTP (POST for `/overview/charts*`). */
  const res = await apiClient.get<T>(endPoint, { params });
  return res.data;
};

export const getEventsPageViaWebSocket = async <T = any>(payload: any): Promise<T> => {
  // NOTE: if the backend expects a different `type` string, update it here.
  return getRealtimeEventsViaWebSocket<T>("events_page_request", payload);
};

